import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { ApiError } from '@shared/api/client';
import { isPermissionRefusal, resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber, formatPercent } from '@shared/format';
import {
  DTable,
  DTableBody,
  DTableFoot,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { ErrorBanner } from '@shared/ui/ErrorBanner';
import { ShareBar } from '@shared/ui/ShareBar';
import { StatCard, StatGrid } from '@shared/ui/stat';

import { copy } from './copy';
import { errors } from './errors';
import type { ReportSectionSpec } from './Index.reports';
import { reportKey, useReport } from './queries';
import type { ReportPayload, ReportTile } from './types';

const TABLE_COLUMNS = 4;

/**
 * One console section: a row of figures over a breakdown table, with the route
 * to the surface that acts on it.
 *
 * Every figure is Arabic-Indic through the shared formatter, and the share bar's
 * fill is ASCII because a stylesheet parses it — the two layers never convert
 * into each other.
 */
export function ReportSection({
  spec,
  beforeTable,
  afterTable,
}: {
  spec: ReportSectionSpec;
  /** Actions the section carries between its figures and its table. */
  beforeTable?: ReactNode;
  /**
   * The line beneath the table. It states where a figure the table does not hold
   * *is* counted, never why this table does not hold it — an operator needs the
   * destination, not the rationale for an absence they never looked for.
   */
  afterTable?: ReactNode;
}) {
  const query = useReport(spec.id);
  const queryClient = useQueryClient();
  const headingId = `report-${spec.id}`;

  return (
    <section aria-labelledby={headingId} className="mt-section-gap first:mt-0">
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 id={headingId} className="font-display text-h2 font-bold">
          {spec.heading}
        </h2>
        <Link
          to={spec.linkTo}
          className="hit-target ms-auto flex items-center rounded-sm px-3 text-xs font-semibold text-ink hover:bg-hover-bg"
        >
          {spec.linkLabel}
        </Link>
      </div>

      {query.isError ? (
        <SectionError
          error={query.error}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: reportKey(spec.id) })}
        />
      ) : null}

      {query.isPending ? <SectionLoading spec={spec} /> : null}

      {query.data ? (
        <SectionData spec={spec} payload={query.data} beforeTable={beforeTable} afterTable={afterTable} />
      ) : null}
    </section>
  );
}

function SectionError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, errors);

  return (
    <ErrorBanner
      message={resolved.message}
      tone={resolved.tone === 'danger' ? 'danger' : 'neutral'}
      kind={isPermissionRefusal(code, status) ? 'permission' : 'failure'}
      onRetry={onRetry}
    />
  );
}

/**
 * Skeleton rows at the real column count, so the table does not reflow when the
 * payload lands. One polite announcement per section, and the shapes themselves
 * are `aria-hidden`.
 */
function SectionLoading({ spec }: { spec: ReportSectionSpec }) {
  return (
    <>
      <p role="status" className="sr-only">
        {copy.states.loading}
      </p>
      <DTableScroll>
        <DTable caption={spec.caption}>
          <DTableHead>
            <tr>
              <th scope="col">{spec.rowColumn}</th>
              <th scope="col">{spec.valueColumn}</th>
              <th scope="col" className="w-[9rem]">
                {copy.columns.share}
              </th>
              <th scope="col">{copy.columns.percent}</th>
            </tr>
          </DTableHead>
          <DTableBody>
            <RowSkeleton columns={TABLE_COLUMNS} />
          </DTableBody>
        </DTable>
      </DTableScroll>
    </>
  );
}

function SectionData({
  spec,
  payload,
  beforeTable,
  afterTable,
}: {
  spec: ReportSectionSpec;
  payload: ReportPayload;
  beforeTable?: ReactNode;
  afterTable?: ReactNode;
}) {
  // Queue depths are not kinds of the thing this table partitions, so they are
  // neither rows of it nor terms of its denominator — see the spec's own note.
  const rows = payload.breakdown.rows.filter((row) => !spec.queueDepthRowKeys.includes(row.key));
  const queueRows = payload.breakdown.rows.filter((row) => spec.queueDepthRowKeys.includes(row.key));
  const denominator = resolveDenominator(spec, { ...payload, breakdown: { rows } });
  const total = spec.totalTileKey ? findTile(payload.tiles, spec.totalTileKey) : undefined;

  return (
    <>
      {payload.tiles.length > 0 ? (
        <StatGrid columns={spec.statColumns}>
          {payload.tiles.map((tile) => (
            <StatCard
              key={tile.key}
              label={copy.tiles[tile.key] ?? tile.key}
              value={<bdi>{formatNumber(tile.value)}</bdi>}
              note={copy.pendingNotes[tile.key]}
              pending={spec.pendingTileKeys.includes(tile.key)}
              // Eleven display-face glyphs do not fit half a 375 px window.
              wide={spec.valuesAreMoney && tile.key === spec.totalTileKey}
            />
          ))}
        </StatGrid>
      ) : null}

      {beforeTable ? <div className="my-4">{beforeTable}</div> : null}

      {rows.length === 0 ? (
        <EmptyState message={copy.states.empty} />
      ) : (
        <DTableScroll>
          <DTable caption={spec.caption}>
            <DTableHead>
              <tr>
                <th scope="col">{spec.rowColumn}</th>
                <th scope="col">{spec.valueColumn}</th>
                <th scope="col" className="w-[9rem]">
                  {copy.columns.share}
                </th>
                <th scope="col">{copy.columns.percent}</th>
              </tr>
            </DTableHead>

            <DTableBody>
              {rows.map((row) => {
                const ratio = denominator > 0 ? row.value / denominator : 0;

                return (
                  <tr key={row.key}>
                    <DTableRowHeader>{row.label}</DTableRowHeader>
                    <td className="whitespace-nowrap [font-variant-numeric:tabular-nums]">
                      <bdi>{formatNumber(row.value)}</bdi>
                    </td>
                    <td>
                      <ShareBar ratio={ratio} tone={spec.shareTone} />
                    </td>
                    <td className="whitespace-nowrap [font-variant-numeric:tabular-nums]">
                      <bdi>{formatPercent(ratio)}</bdi>
                    </td>
                  </tr>
                );
              })}
            </DTableBody>

            {total ? (
              <DTableFoot>
                <tr>
                  <th scope="row">{copy.total}</th>
                  <td className="whitespace-nowrap [font-variant-numeric:tabular-nums]">
                    <bdi>{formatNumber(total.value)}</bdi>
                  </td>
                  <td />
                  <td />
                </tr>
              </DTableFoot>
            ) : null}
          </DTable>
        </DTableScroll>
      )}

      {queueRows.length > 0 ? (
        <section aria-labelledby={`${spec.id}-queues`} className="mt-6">
          <h3 id={`${spec.id}-queues`} className="mb-1 font-display text-h4">
            {copy.queueDepth.heading}
          </h3>
          <p className="mb-3 max-w-measure text-xs text-ink-muted">{copy.queueDepth.lede}</p>

          <DTableScroll>
            <DTable caption={copy.queueDepth.caption}>
              <DTableHead>
                <tr>
                  <th scope="col">{copy.queueDepth.rowColumn}</th>
                  <th scope="col">{copy.queueDepth.valueColumn}</th>
                </tr>
              </DTableHead>
              <DTableBody>
                {queueRows.map((row) => (
                  <tr key={row.key}>
                    <DTableRowHeader>{row.label}</DTableRowHeader>
                    <td className="whitespace-nowrap [font-variant-numeric:tabular-nums]">
                      <bdi>{formatNumber(row.value)}</bdi>
                    </td>
                  </tr>
                ))}
              </DTableBody>
            </DTable>
          </DTableScroll>
        </section>
      ) : null}

      {afterTable ? <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">{afterTable}</p> : null}
    </>
  );
}

function findTile(tiles: readonly ReportTile[], tileKey: string): ReportTile | undefined {
  return tiles.find((tile) => tile.key === tileKey);
}

/**
 * The whole a row is a share of.
 *
 * The named tile when the section declares one — because the content breakdown
 * holds two partitions in one array and each sums to the same total. The sum of
 * the rows otherwise, which is correct wherever the rows *are* the whole. Zero
 * when neither is available, which renders an empty bar rather than a division
 * the reader cannot check.
 */
function resolveDenominator(spec: ReportSectionSpec, payload: ReportPayload): number {
  if (spec.denominatorTileKey) {
    const tile = findTile(payload.tiles, spec.denominatorTileKey);
    if (tile && tile.value > 0) return tile.value;
  }

  return payload.breakdown.rows.reduce((sum, row) => sum + row.value, 0);
}
